import { useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useMarket } from "../lib/store";
import { usePortfolio } from "../lib/usePortfolio";
import { useSubscribe } from "../lib/socket";
import { fmt, signed, tone, pct, isDerivative } from "../lib/format";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "../components/ui/alert-dialog";
import { Panel, PageHeader, th, td, btnGhost, Empty } from "../components/Panel";
import { Odometer, Magnetic } from "../components/motion";
import { Price, StaleTag } from "../components/Price";
import { ShareRowButton } from "../components/community/TgBits";
import { openShareFor } from "../lib/community";
import { DiwaliCard } from "../components/diwali/DiwaliOffer";

export function pnlOf(p, t) {
  const ltp = t?.ltp ?? p.avg_price;
  const unreal = p.product === "CNC" ? 0 : (ltp - p.avg_price) * p.qty;
  return { ltp, unreal, total: (p.realised_today ?? 0) + unreal };
}

function ConvertDialog({ p, onClose }) {
  const meta = useMarket((s) => (p ? s.meta[p.instrument_key] : null));
  const [qty, setQty] = useState("");
  if (!p) return null;
  const der = isDerivative(meta) || p.segment?.endsWith("_FO");
  const to = p.product === "MIS" ? (der ? "NRML" : "CNC") : "MIS";
  const q = Number(qty || Math.abs(p.qty));
  const go = async () => {
    try {
      await api.post("/positions/convert", { instrument_key: p.instrument_key, from_product: p.product, to_product: to, quantity: q });
      toast.success(`Converted ${q} ${p.symbol} ${p.product} → ${to}`);
      onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-card sm:max-w-sm" data-testid="convert-dialog">
        <DialogHeader><DialogTitle className="font-display">Convert {p.symbol}</DialogTitle>
          <DialogDescription className="text-[12px]">{p.product} → {to}. Margin is re-checked; converting to CNC debits the full value from cash.</DialogDescription></DialogHeader>
        <input data-testid="convert-qty-input" type="number" placeholder={String(Math.abs(p.qty))} value={qty} onChange={(e) => setQty(e.target.value)}
          className="w-full h-9 px-2.5 rounded-md border border-input bg-background num text-[13px] outline-none focus:border-primary" />
        <button data-testid="convert-submit" onClick={go} className="h-9 rounded-md bg-primary text-primary-foreground font-semibold text-[13px]">Convert to {to}</button>
      </DialogContent>
    </Dialog>
  );
}

export default function Positions() {
  const all = usePortfolio("/positions") || [];
  const [view, setView] = useState("net");
  const [conv, setConv] = useState(null);
  const today = new Date().toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" });
  const rows = view === "day" ? all.filter((p) => p.day === today && (p.buy_qty || p.sell_qty)) : all;
  const ticks = useMarket((s) => s.ticks);
  const openOrder = useMarket((s) => s.openOrder);
  const [confirm, setConfirm] = useState(false);
  useSubscribe(rows.map((p) => p.instrument_key));
  const calc = rows.map((p) => ({ p, t: ticks[p.instrument_key], ...pnlOf(p, ticks[p.instrument_key]) }));
  const mtm = calc.reduce((a, r) => a + r.total, 0);
  const realised = calc.reduce((a, r) => a + (r.p.realised_today || 0), 0);
  const unreal = calc.reduce((a, r) => a + r.unreal, 0);
  const openCount = rows.filter((p) => p.qty !== 0).length;
  const exitAll = async () => {
    try { const { data } = await api.post("/positions/exit-all"); toast.message(`Simulated order · exit orders placed for ${data.length} positions`); } catch (e) { toast.error(errMsg(e)); }
    setConfirm(false);
  };
  return (
    <div className="p-4 lg:p-6" data-testid="positions-page">
      <PageHeader title={`Positions (${rows.length})`}>
        <Tabs value={view} onValueChange={setView}><TabsList className="h-8">
          <TabsTrigger value="net" data-testid="positions-tab-net" className="text-[11px]">Net</TabsTrigger>
          <TabsTrigger value="day" data-testid="positions-tab-day" className="text-[11px]">Day</TabsTrigger>
        </TabsList></Tabs>
        <button data-testid="positions-exit-all" disabled={!openCount} onClick={() => setConfirm(true)} className={`${btnGhost} text-down disabled:opacity-40`}>Exit all</button>
      </PageHeader>
      <Panel className="mb-4">
        <div className="grid grid-cols-3 divide-x divide-border">
          <div className="px-4 py-3"><div className="eyebrow">Total MTM</div><div data-testid="positions-mtm-total" className={`num text-[20px] font-semibold ${tone(mtm)}`}><Odometer text={signed(mtm)} /></div></div>
          <div className="px-4 py-3"><div className="eyebrow">Realised</div><div className={`num text-[16px] ${tone(realised)}`} data-testid="positions-realised">{signed(realised)}</div></div>
          <div className="px-4 py-3"><div className="eyebrow">Unrealised</div><div className={`num text-[16px] ${tone(unreal)}`} data-testid="positions-unrealised">{signed(unreal)}</div></div>
        </div>
      </Panel>
      <Panel>
        {!rows.length ? (
          <div className="py-12 flex flex-col items-center gap-4">
            <Empty testid="positions-empty">No positions yet. Place an order from the watchlist.</Empty>
            <Magnetic data-testid="positions-first-trade" onClick={() => useMarket.getState().setSearch(true)} className="-mt-10 h-10 px-5 rounded-full bg-gold text-[#0b0b0d] font-semibold text-[12.5px] shadow-gold">Place your first trade</Magnetic>
          </div>) : (
          <div className="overflow-x-auto thin-scroll">
            <table className="w-full" data-testid="positions-table">
              <thead className="border-b border-border"><tr>
                {["Product", "Instrument", "Qty", "Avg", "LTP", "P&L", "Chg%", ""].map((h, i) => <th key={i} className={`${th} ${i >= 2 && i <= 6 ? "text-right" : ""}`}>{h}</th>)}
              </tr></thead>
              <tbody>
                {calc.map(({ p, t, total }) => (
                  <tr key={p.id} className={`border-b border-border/60 hover:bg-accent/40 ${p.qty === 0 ? "opacity-55" : ""}`} data-testid={`position-row-${p.symbol}`}>
                    <td className={td}><span className="text-[10px] font-semibold rounded px-1.5 py-0.5 bg-muted">{p.product}</span></td>
                    <td className={`${td} font-medium`}>{p.symbol} <span className="text-[10px] text-muted-foreground">{p.exchange}</span></td>
                    <td className={`${td} num text-right ${p.qty > 0 ? "text-up" : p.qty < 0 ? "text-down" : ""}`}>{p.qty}</td>
                    <td className={`${td} num text-right`}>{fmt(p.avg_price)}</td>
                    <td className={`${td} text-right`}><Price value={t?.ltp} prev={t?.prevLtp} /><StaleTag /></td>
                    <td className={`${td} num text-right font-semibold ${tone(total)}`} data-testid={`position-pnl-${p.symbol}`}>{signed(total)}</td>
                    <td className={`${td} num text-right ${tone(t?.chp)}`}>{pct(t?.chp)}</td>
                    <td className={`${td} text-right`}>
                      {p.qty !== 0 && (<span className="flex justify-end gap-1">
                        <ShareRowButton testid={`position-share-${p.symbol}`} onClick={() => openShareFor({ positionId: p.id }).catch((e) => toast.error(errMsg(e)))} />
                        <button className={btnGhost} data-testid={`position-convert-${p.symbol}`} onClick={() => setConv(p)}>Convert</button>
                        <button className={btnGhost} data-testid={`position-add-${p.symbol}`} onClick={() => openOrder({ key: p.instrument_key, side: p.qty > 0 ? "BUY" : "SELL", product: p.product })}>Add</button>
                        <button className={`${btnGhost} text-down`} data-testid={`position-exit-${p.symbol}`}
                          onClick={() => openOrder({ key: p.instrument_key, side: p.qty > 0 ? "SELL" : "BUY", product: p.product, qty: Math.abs(p.qty), orderType: "MARKET", exit: true })}>Exit</button>
                      </span>)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>)}
      </Panel>
      <div className="mt-6 mx-auto max-w-[520px] empty:hidden"><DiwaliCard /></div>
      {conv && <ConvertDialog p={conv} onClose={() => setConv(null)} />}
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent className="bg-card">
          <AlertDialogHeader><AlertDialogTitle>Exit all positions?</AlertDialogTitle>
            <AlertDialogDescription>Market orders will be placed to square off {openCount} open intraday/carry-forward positions.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel data-testid="exit-all-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="exit-all-confirm" onClick={exitAll} className="bg-down hover:bg-down/90">Exit all</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
